export interface DispatchedVisibility {
  level: string
  id?: number | string | null
  visible: boolean
}

export type SetVisibleByLevelOptions = {
  /**
   * Panel/page setVisible normally propagates to children (Swing VisualEntity).
   * For post-script replay (`applyDispatchedVisibilities`), keep false: En already
   * applied live setVisible+propagate; re-propagate would re-show sibling labels
   * that the same script later hid by name.
   */
  propagateChildren?: boolean
}

/** Non-numeric id in triad setVisible — field / jsName, not XML id. */
export function isNameKeyedVisibilityId(id: unknown): boolean {
  const idStr = String(id ?? '').trim()
  return idStr !== '' && !/^\d+$/.test(idStr)
}

export interface ApplyDispatchedVisibilitiesDeps {
  formProvider?: {
    setVisible?: (name: string, visible: boolean) => void
    setVisibleByLevel?: (
      level: string,
      id: string | number,
      visible: boolean,
      options?: SetVisibleByLevelOptions,
    ) => void
    getOverlays?: () => Record<
      string,
      { name: string; overlayId: string; formId?: string; setVisible?: (v: boolean) => void }
    >
  }
  overlayIds?: Iterable<string | number>
  formKeys?: Array<string | number | undefined | null>
}

function readTriadEntry(entry: unknown): DispatchedVisibility | null {
  if (entry == null) return null

  if (Array.isArray(entry) && entry.length >= 3) {
    return {
      level: String(entry[0] ?? ''),
      id: entry[1] == null ? null : entry[1],
      visible: Boolean(entry[2]),
    }
  }

  if (typeof entry === 'object') {
    const obj = entry as Record<string, unknown>
    if ('level' in obj || 'visible' in obj) {
      return {
        level: String(obj.level ?? obj.element ?? ''),
        id: obj.id == null ? null : (obj.id as number | string),
        visible: Boolean(obj.visible),
      }
    }
    if ('first' in obj && 'third' in obj) {
      return {
        level: String((obj as { first?: unknown }).first ?? ''),
        id: (obj as { second?: unknown }).second == null
          ? null
          : ((obj as { second?: unknown }).second as number | string),
        visible: Boolean((obj as { third?: unknown }).third),
      }
    }
  }

  return null
}

export function normalizeDispatchedVisibilities(raw: unknown): DispatchedVisibility[] {
  if (!raw) return []
  if (!Array.isArray(raw)) return []

  const result: DispatchedVisibility[] = []
  for (const entry of raw) {
    const normalized = readTriadEntry(entry)
    if (normalized && normalized.level) {
      result.push(normalized)
    }
  }
  return result
}

function buildOverlayIdSet(
  deps: ApplyDispatchedVisibilitiesDeps,
): Set<string> {
  const ids = new Set<string>()
  for (const id of deps.overlayIds ?? []) {
    ids.add(String(id))
  }
  const overlays = deps.formProvider?.getOverlays?.()
  if (overlays) {
    for (const entry of Object.values(overlays)) {
      if (entry?.overlayId) ids.add(String(entry.overlayId))
    }
  }
  return ids
}

function isOverlayId(id: string | number, overlayIds: Set<string>): boolean {
  return overlayIds.has(String(id))
}

/**
 * Panels first (may propagate to children), then element/action/name — last wins.
 * Otherwise panel show in finally-replay re-shows labels that onLoad already hid.
 */
export function orderDispatchedVisibilitiesForApply(
  queue: DispatchedVisibility[],
): DispatchedVisibility[] {
  const panels: DispatchedVisibility[] = []
  const restLastWins = new Map<string, DispatchedVisibility>()

  for (const entry of queue) {
    const levelUpper = String(entry.level ?? '').toUpperCase()
    if (levelUpper === 'PANEL') {
      panels.push(entry)
      continue
    }
    const key =
      entry.id != null && String(entry.id).trim() !== ''
        ? `${levelUpper}:${String(entry.id)}`
        : `NAME:${String(entry.level ?? '')}`
    restLastWins.set(key, entry)
  }

  return [...panels, ...restLastWins.values()]
}

export function applyDispatchedVisibilities(
  raw: unknown,
  deps: ApplyDispatchedVisibilitiesDeps,
): void {
  const queue = orderDispatchedVisibilitiesForApply(normalizeDispatchedVisibilities(raw))
  if (queue.length === 0) return

  const overlayIds = buildOverlayIdSet(deps)
  // Replay only — do not re-propagate panel→children (see SetVisibleByLevelOptions).
  const byLevelOptions: SetVisibleByLevelOptions = { propagateChildren: false }

  for (const { level, id, visible } of queue) {
    const levelUpper = String(level ?? '').toUpperCase()

    if (id != null && id !== '') {
      const idStr = String(id)

      if (
        isOverlayId(id, overlayIds) ||
        levelUpper === 'OVERLAYPAGE' ||
        (levelUpper === 'PAGE' && isOverlayId(id, overlayIds))
      ) {
        if (typeof deps.formProvider?.setVisibleByLevel === 'function') {
          deps.formProvider.setVisibleByLevel(level, id, visible, byLevelOptions)
        } else {
          deps.formProvider?.setVisible?.(idStr, visible)
        }
        continue
      }

      if (levelUpper === 'PAGE') {
        if (typeof deps.formProvider?.setVisibleByLevel === 'function') {
          deps.formProvider.setVisibleByLevel(level, id, visible, byLevelOptions)
        } else {
          deps.formProvider?.setVisible?.(idStr, visible)
        }
        continue
      }

      if (typeof deps.formProvider?.setVisibleByLevel === 'function') {
        deps.formProvider.setVisibleByLevel(level, id, visible, byLevelOptions)
      } else {
        deps.formProvider?.setVisible?.(idStr, visible)
      }
      const levelNorm = String(level ?? '').trim().toLowerCase()
      if (
        (levelNorm === 'element' || levelNorm === 'action') &&
        isNameKeyedVisibilityId(idStr)
      ) {
        deps.formProvider?.setVisible?.(idStr, visible)
      }
      continue
    }

    deps.formProvider?.setVisible?.(level, visible)
  }
}
